import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './SolutionsModal.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faMeteor,
  faInfoCircle,
  faNewspaper,
  faRoute,
  faWandMagicSparkles,
  faToolbox,
} from '@fortawesome/free-solid-svg-icons';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

// Traducciones completas del modal
const modalTranslations3 = {
  es: {
    header: 'Optimiza el plan de desarrollo',
    intro:
      'Trabaja mano a mano con cada empleado para crear objetivos claros y alcanzables que impulsen su crecimiento en solo 90 días. Transformarás el potencial detectado en resultados tangibles creando una cultura de mejora continua y éxito compartido.',
    sections: [
      {
        icon: faRoute,
        title: 'BetherPlan® en 90 Días',
        bullets: [
          'Diagnóstico profundo: La IA de Bether analiza tu evaluación de competencias y el Role Fit Score para detectar las áreas de mayor impacto.',
          'Roadmap personalizado: Definimos hitos semanales y mensuales que garantizan avances medibles.',
          'Revisión y ajuste continuo: Feedback automático y coaching sugerido para corregir la ruta en tiempo real.',
        ],
      },
      {
        icon: faWandMagicSparkles,
        title: 'Planes Personalizados',
        bullets: [
          'Objetivos SMART: Metas específicas, medibles, alcanzables, relevantes y con plazo definido.',
          'Adaptación dinámica: Si cambia la prioridad del negocio o las necesidades del colaborador, el plan se ajusta al vuelo.',
          'Visualiza el progreso: Gráficos de avance y hitos cumplidos, tanto para el manager como para el empleado.',
        ],
      },
      {
        icon: faToolbox,
        title: 'BetherTools®: Acciones Cotidianas',
        bullets: [
          'Micro-tareas diarias: Ejercicios prácticos que refuerzan la adquisición de habilidades clave.',
          'Recordatorios inteligentes: Notificaciones contextuales que mantienen el foco en los objetivos.',
          'Recursos “just-in-time”: Vídeos, plantillas y lecturas recomendadas justo cuando más las necesitas.',
        ],
      },
    ],
    benefitsText:
      'Mira todos los beneficios que puedes obtener al poner en práctica una estrategia como esta:',
    sourceLabel: 'Fuente',
    kpis: [
      {
        metric: 'Time to readiness',
        baseline: 65,
        improved: 45,
        unit: 'days',
        center: '-30%',
        tooltipText:
          'Las BetherTools® y los hitos semanales reducen el tiempo hasta la competencia un 30%, de 65 a 45 días.',
        link: 'https://umbrex.com/resources/key-performance-indicators/human-resources-key-performance-indicators/time-to-proficiency',
      },
      {
        metric: '1:1 Quality',
        baseline: 25,
        improved: 75,
        unit: '%',
        center: '×3',
        tooltipText:
          'Con BetherPlan® generas agendas conjuntas y sugerencias de conversación en IA, multiplicas por 2 la frecuencia semanal y triplicas la satisfacción del empleado.',
        link: 'https://www.gallup.com/workplace/657629/post-pandemic-workplace-experiment-continues.aspx',
      },
      {
        metric: 'Satisfacción con el Plan de Desarrollo',
        baseline: 48.3,
        improved: 85,
        unit: '%',
        center: '+36%',
        tooltipText:
          'Los datos en vivo y los hitos visuales de BetherPlan® y BetherTools® hacen que el 85 % de tu plantilla valore positivamente su avance mensual.',
        link: 'https://www.conference-board.org/press/job-satisfaction-hits-all-time-high',
      },
    ],
  },
  en: {
    header: 'Optimize the Development Plan',
    intro:
      'Work hand in hand with each employee to create clear and achievable goals that drive their growth in just 90 days. You’ll transform detected potential into tangible results by fostering a culture of continuous improvement and shared success.',
    sections: [
      {
        icon: faRoute,
        title: 'BetherPlan® in 90 Days',
        bullets: [
          'In-depth diagnosis: Bether’s AI analyzes your competency assessment and Role Fit Score to pinpoint areas of highest impact.',
          'Personalized roadmap: We define weekly and monthly milestones to ensure measurable progress.',
          'Continuous review & adjustment: Automated feedback and suggested coaching to correct the course in real time.',
        ],
      },
      {
        icon: faWandMagicSparkles,
        title: 'Personalized Plans',
        bullets: [
          'SMART objectives: Specific, measurable, achievable, relevant, and time-bound goals.',
          'Dynamic adaptation: Plans adjust on the fly to shifting business priorities or employee needs.',
          'Progress tracking: Visual charts of milestones achieved for both managers and employees.',
        ],
      },
      {
        icon: faToolbox,
        title: 'BetherTools®: Daily Actions',
        bullets: [
          'Daily micro-tasks: Practical exercises that reinforce key skill acquisition.',
          'Smart reminders: Contextual notifications to keep focus on goals.',
          'Just-in-time resources: Videos, templates, and reading materials when you need them most.',
        ],
      },
    ],
    benefitsText:
      'See all the benefits you can gain by implementing a strategy like this:',
    sourceLabel: 'Source',
    kpis: [
      {
        metric: 'Time to Readiness',
        baseline: 65,
        improved: 45,
        unit: 'days',
        center: '-30%',
        tooltipText:
          'BetherTools® and weekly milestones reduce time to proficiency by 30%, from 65 to 45 days.',
        link: 'https://umbrex.com/resources/key-performance-indicators/human-resources-key-performance-indicators/time-to-proficiency',
      },
      {
        metric: '1:1 Quality',
        baseline: 25,
        improved: 75,
        unit: '%',
        center: '×3',
        tooltipText:
          'With BetherPlan® you generate joint agendas and AI-driven conversation suggestions, doubling weekly frequency and tripling employee satisfaction.',
        link: 'https://www.gallup.com/workplace/657629/post-pandemic-workplace-experiment-continues.aspx',
      },
      {
        metric: 'Development Plan Satisfaction',
        baseline: 48.3,
        improved: 85,
        unit: '%',
        center: '+36%',
        tooltipText:
          'Live data and visual milestones from BetherPlan® and BetherTools® ensure 85% of your workforce values their monthly progress positively.',
        link: 'https://www.conference-board.org/press/job-satisfaction-hits-all-time-high',
      },
    ],
  },
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const entry = payload[0];
    return (
      <div className="tooltip-box">
        <strong>{entry.name}</strong>: {entry.value.toFixed(1)}
        {entry.payload.unit}
      </div>
    );
  }
  return null;
};

const KPICharts = ({ kpis, sourceLabel }) => {
  const [infoVisible, setInfoVisible] = useState({});
  const toggleInfo = (metric) =>
    setInfoVisible((prev) => ({ ...prev, [metric]: !prev[metric] }));

  return (
    <div className="smodal-tei-charts">
      {kpis.map(
        ({ metric, baseline, improved, unit, center, tooltipText, link }) => {
          const outerData = [
            { name: 'Bether Average', value: improved, unit },
            { name: '', value: 100 - improved },
          ];
          const innerData = [
            { name: 'Business Average', value: baseline, unit },
            { name: '', value: 100 - baseline },
          ];
          return (
            <div key={metric} className="smodal-tei-chart-card">
              <h4 className="smodal-tei-chart-title">{metric}</h4>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={outerData}
                    dataKey="value"
                    nameKey="name"
                    startAngle={90}
                    endAngle={-270}
                    innerRadius="65%"
                    outerRadius="95%"
                    paddingAngle={2}
                    isAnimationActive={false}
                  >
                    {outerData.map((_, idx) => (
                      <Cell
                        key={idx}
                        fill={idx === 0 ? '#ff2300' : '#e0e0e0'}
                      />
                    ))}
                  </Pie>
                  <Pie
                    data={innerData}
                    dataKey="value"
                    nameKey="name"
                    startAngle={90}
                    endAngle={-270}
                    innerRadius="45%"
                    outerRadius="63%"
                    paddingAngle={2}
                    isAnimationActive={false}
                  >
                    {innerData.map((_, idx) => (
                      <Cell
                        key={idx}
                        fill={idx === 0 ? '#555555' : '#e0e0e0'}
                      />
                    ))}
                  </Pie>
                  <text
                    x="50%"
                    y="50%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="smodal-tei-chart-center"
                  >
                    {center}
                  </text>
                  <RechartsTooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="info-icon"
                onClick={() => toggleInfo(metric)}
              />
              {infoVisible[metric] && (
                <div className="tooltip-popup">
                  <div className="tooltip-text">{tooltipText}</div>
                  {link && (
                    <button
                      className="source-button"
                      onClick={() => window.open(link, '_blank')}
                    >
                      <FontAwesomeIcon icon={faNewspaper} />
                      &nbsp;{sourceLabel}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        }
      )}
    </div>
  );
};

const SolutionsModal3 = ({ isOpen, onClose, language = 'es' }) => {
  if (!isOpen) return null;
  const t = modalTranslations3[language];
  return (
    <div className="smodal-overlay">
      <div className="smodal-content">
        <button className="smodal-close" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2 className="smodal-header">{t.header}</h2>
        <p className="smodal-tei-intro">{t.intro}</p>
        {t.sections.map((sec, idx) => (
          <div key={idx} className="smodal-section">
            <FontAwesomeIcon icon={sec.icon} className="section-icon" />
            <div className="section-content">
              <h3 className="section-title">{sec.title}</h3>
              {sec.bullets.length > 0 && (
                <ul className="section-list">
                  {sec.bullets.map((bullet, bidx) => (
                    <li key={bidx}>{bullet}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
        <div className="smodal-tei-box">
          <h3 className="smodal-tei-title">
            <FontAwesomeIcon icon={faMeteor} className="tei-icon" /> TEI: Total
            Economic Impact
          </h3>
          <p className="smodal-tei-intro">{t.benefitsText}</p>
          <KPICharts kpis={t.kpis} sourceLabel={t.sourceLabel} />
        </div>
      </div>
    </div>
  );
};

SolutionsModal3.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  language: PropTypes.oneOf(['es', 'en']),
};

export default SolutionsModal3;
